import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { 
  Package, Truck, CheckCircle2, AlertCircle, Search, Save 
} from 'lucide-react';

interface Shipment {
  id: number;
  trackingNumber: string;
  receiverName: string;
  city: string;
  price: number;
  status: string;
}

interface Delegate {
  id: number;
  name: string;
}

export function CreateDrPage() {
  const navigate = useNavigate();
  
  const [delegates, setDelegates] = useState<Delegate[]>([]);
  const [shipments, setShipments] = useState<Shipment[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  
  const [selectedDelegateId, setSelectedDelegateId] = useState('');
  const [selectedShipmentIds, setSelectedShipmentIds] = useState<number[]>([]);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // 1. جلب المناديب
      try {
        const delegatesRes = await api.get('/api/Delegates');
        const delData = delegatesRes.data.data?.items || delegatesRes.data.data || [];
        setDelegates(delData.length > 0 ? delData : [
          { id: 101, name: 'الكابتن / أحمد مصطفى (القاهرة)' },
          { id: 102, name: 'الكابتن / محمد محمود (الجيزة)' }
        ]);
      } catch {
        setDelegates([
          { id: 101, name: 'الكابتن / أحمد مصطفى (القاهرة)' },
          { id: 102, name: 'الكابتن / محمد محمود (الجيزة)' }
        ]);
      }

      // 2. جلب الشحنات الجديدة فقط (statusId = 1)
      try {
        const shipmentsRes = await api.get('/api/Shipments', { 
          params: { statusId: 1, pageSize: 100 } 
        });
        const shipData = shipmentsRes.data.data?.items || shipmentsRes.data.data || [];
        setShipments(shipData.length > 0 ? shipData : [
          { id: 501, trackingNumber: 'TGX-99211', receiverName: 'أحمد علي جلال', city: 'القاهرة - مدينة نصر', price: 250, status: 'new' },
          { id: 502, trackingNumber: 'TGX-99212', receiverName: 'هاني شاكر المحمدي', city: 'الجيزة - الدقي', price: 420, status: 'new' }
        ]);
      } catch (err) {
        console.error("Error loading shipments:", err);
        setShipments([
          { id: 501, trackingNumber: 'TGX-99211', receiverName: 'أحمد علي جلال', city: 'القاهرة - مدينة نصر', price: 250, status: 'new' }
        ]);
      }
    } catch (error) {
      console.error("Error loading DRS data:", error);
    } finally {
      setLoading(false);
    }
  };

  const toggleShipment = (id: number) => {
    if (selectedShipmentIds.includes(id)) {
      setSelectedShipmentIds(selectedShipmentIds.filter(sid => sid !== id));
    } else {
      setSelectedShipmentIds([...selectedShipmentIds, id]);
    }
  };

  const toggleAll = () => {
    const filteredIds = filteredShipments.map(s => s.id);
    const allSelected = filteredIds.every(id => selectedShipmentIds.includes(id));
    
    if (allSelected) {
      setSelectedShipmentIds(selectedShipmentIds.filter(id => !filteredIds.includes(id)));
    } else {
      const newSelected = [...new Set([...selectedShipmentIds, ...filteredIds])];
      setSelectedShipmentIds(newSelected);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedDelegateId) {
      alert('الرجاء اختيار المندوب أولاً');
      return;
    }
    if (selectedShipmentIds.length === 0) {
      alert('الرجاء تحديد شحنة واحدة على الأقل');
      return;
    }

    try {
      setSubmitting(true);
      
      const payload = {
        delegateId: Number(selectedDelegateId),
        shipmentIds: selectedShipmentIds,
        drDate: new Date().toISOString(),
        notes: ''
      };

      // استخدام api.post مباشرة لضمان إرسال المسار الصحيح والتوكن
      await api.post('/api/drs', payload);
      
      alert('تم إسناد الشحنات للمندوب بنجاح وتوليد خط السير (DRS)!');
      navigate('/drs');
    } catch (error: any) {
      console.error("Submission error:", error);
      const errorMsg = error.response?.data?.message || 'حدث خطأ أثناء حفظ إيصال التوصيل';
      alert(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  const filteredShipments = shipments.filter(s => 
    s.trackingNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.receiverName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.city?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="p-12 text-center text-gray-500">
        <div className="animate-spin w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full mx-auto mb-3"></div>
        جاري جلب المناديب والشحنات المتاحة...
      </div>
    );
  }

  return (
    <div className="p-6 max-w-5xl mx-auto text-right" dir="rtl">
      <div className="flex items-center justify-between mb-6 border-b pb-4">
        <div className="flex items-center gap-2">
          <Truck className="w-6 h-6 text-orange-500" />
          <div>
            <h1 className="text-2xl font-bold text-gray-800">إنشاء إيصال توصيل جديد (DRS)</h1>
            <p className="text-xs text-gray-500 mt-0.5">توزيع الشحنات الجاهزة وإسناد خط سير للمندوب</p>
          </div>
        </div>
        <button 
          type="button" 
          onClick={() => navigate('/drs')} 
          className="text-xs font-bold text-gray-600 hover:text-gray-900 flex items-center gap-1 bg-gray-100 px-3 py-2 rounded-xl"
        >
          العودة للقائمة
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* اختيار المندوب */}
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
          <h2 className="text-sm font-bold text-gray-800 flex items-center gap-1.5 mb-4">
            <Truck className="w-4 h-4 text-orange-500" /> 1. اختيار مندوب التوصيل
          </h2>
          <div className="max-w-md">
            <select
              required
              value={selectedDelegateId}
              onChange={(e) => setSelectedDelegateId(e.target.value)}
              className="w-full p-3 border rounded-xl bg-white text-sm outline-none focus:border-orange-500 font-semibold text-gray-700"
            >
              <option value="">-- اضغط لاختيار كابتن التوصيل --</option>
              {delegates.map((d) => (
                <option key={d.id} value={d.id}>{d.name}</option>
              ))}
            </select>
          </div>
        </div>

        {/* اختيار الشحنات */}
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-4">
            <h2 className="text-sm font-bold text-gray-800 flex items-center gap-1.5">
              <Package className="w-4 h-4 text-orange-500" /> 2. تحديد الشحنات المشمولة في خط السير
            </h2>
            <div className="relative w-full md:w-72">
              <Search className="w-4 h-4 text-gray-400 absolute right-3 top-2.5" />
              <input
                type="text"
                placeholder="بحث برقم الشحنة، المستلم، المحافظة..."
                className="w-full pr-9 pl-3 py-2 border rounded-lg text-xs outline-none focus:border-orange-500"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          <div className="border rounded-xl overflow-hidden max-h-96 overflow-y-auto">
            <table className="w-full text-right text-xs">
              <thead className="bg-gray-50 border-b text-gray-700 sticky top-0">
                <tr>
                  <th className="p-3 w-12 text-center">
                    <button
                      type="button"
                      onClick={toggleAll}
                      className="text-orange-500 hover:scale-110 transition-transform"
                    >
                      {filteredShipments.length > 0 && filteredShipments.every(s => selectedShipmentIds.includes(s.id)) ? '☑' : '☐'}
                    </button>
                  </th>
                  <th className="p-3">رقم الشحنة</th>
                  <th className="p-3">اسم المستلم</th>
                  <th className="p-3">المحافظة / العنوان</th>
                  <th className="p-3 text-center">المبلغ</th>
                </tr>
              </thead>
              <tbody className="divide-y text-gray-600">
                {filteredShipments.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="p-6 text-center text-gray-400">
                      لا توجد شحنات مطابقة للبحث حالياً
                    </td>
                  </tr>
                ) : (
                  filteredShipments.map((s) => {
                    const isSelected = selectedShipmentIds.includes(s.id);
                    return (
                      <tr 
                        key={s.id} 
                        onClick={() => toggleShipment(s.id)}
                        className={`hover:bg-orange-50/50 cursor-pointer transition-colors ${isSelected ? 'bg-orange-50' : ''}`}
                      >
                        <td className="p-3 text-center">
                          {isSelected ? (
                            <CheckCircle2 className="w-4 h-4 text-orange-500 mx-auto" />
                          ) : (
                            <AlertCircle className="w-4 h-4 text-gray-300 mx-auto" />
                          )}
                        </td>
                        <td className="p-3 font-mono font-bold text-gray-900">{s.trackingNumber}</td>
                        <td className="p-3 font-medium">{s.receiverName}</td>
                        <td className="p-3 text-orange-600 font-bold">{s.city}</td>
                        <td className="p-3 text-center font-bold">{s.price} ج.م</td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          <div className="mt-3 text-xs font-bold text-gray-600 flex justify-between items-center bg-gray-50 p-3 rounded-lg">
            <span>إجمالي الشحنات المختارة: <span className="text-orange-500 text-sm">{selectedShipmentIds.length}</span> شحنة</span>
            <span>المتاحة للتوزيع: {filteredShipments.length}</span>
          </div>
        </div>

        {/* زر الحفظ */}
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={submitting}
            className="bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white font-bold px-6 py-3 rounded-xl text-xs flex items-center gap-1.5 shadow-md shadow-orange-500/20 transition-all"
          >
            <Save className="w-4 h-4" /> 
            {submitting ? 'جاري إنشاء المانيفست...' : 'حفظ وتوليد إيصال الـ DRS'}
          </button>
        </div>
      </form>
    </div>
  );
}
